import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import axios from 'axios'
import { QueryError } from 'errors'
import posthog from 'posthog-js'
import { Account, Login, RoleName, UpdatePassword } from 'types/auth'
import { NewUser, User } from 'types/users'

export function useLogin() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation<Account, QueryError, Login>({
    mutationKey: ['login'],
    mutationFn: async (data: Login) => (await axios.post('/auth/login', data)).data,
    onSuccess: (account) => {
      queryClient.setQueryData(['account'], account)
      navigate({ to: '/' })
      posthog.identify(account.id, { username: account.username, name: account.firstName + ' ' + account.lastName })
    },
    meta: {
      isErrorToasted: false,
    },
  })
}

export function useLogout() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  return useMutation<void, QueryError, void>({
    mutationKey: ['logout'],
    mutationFn: async () => {
      await axios.post('/auth/logout')
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] })
      queryClient.clear()
      navigate({ to: '/login' })
      posthog.reset()
    },
  })
}

export function useAddUser() {
  const queryClient = useQueryClient()
  return useMutation<User, QueryError, NewUser>({
    mutationKey: ['addUser'],
    mutationFn: async (user) => {
      return (await axios.post('/auth/register', user)).data
    },
    meta: {
      closeModal: true,
      successMessage: 'Użytkownik został dodany',
    },
    onSuccess: (createdUser) => {
      queryClient.setQueryData<Array<User>>(['users'], (data) => {
        if (!data) return [createdUser]
        return [...data, createdUser]
      })
    },
  })
}

export function useUpdatePassword() {
  const queryClient = useQueryClient()
  return useMutation<void, QueryError, UpdatePassword & { id: string }>({
    mutationKey: ['updatePassword'],
    mutationFn: async (data) => {
      await axios.post(`/auth/password/${data.id}`, data)
    },
    meta: {
      closeModal: true,
      successMessage: 'Hasło zostało zmienione',
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] })
    },
  })
}

export const accountQuery = queryOptions<Account>({
  queryKey: ['account'],
  queryFn: async () => (await axios.get('/account')).data,
  retry: 1,
  staleTime: Infinity,
})

export function useAccount() {
  return useSuspenseQuery(accountQuery)
}

export function useIsAdmin() {
  const { data } = useSuspenseQuery({
    ...accountQuery,
    select: (account) => account.roles.some((r) => r.name === RoleName.ADMIN),
  })
  return data
}
